import { useState, type ReactNode } from "react";
import { useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { formatUsd, mediaOptions } from "../../../convex/lib/quote";
import type { ChannelScan } from "../../../convex/lib/scan";
import { GB, asBoostTier, formatBytes, tierLabel } from "../../lib/estimator";

type Strategy = "skip" | "host" | "boost";

/**
 * The oversized-media chooser: for files over the connected server's Discord
 * upload cap, present the three honest options with live per-workspace math —
 * Boost (flat, plays inline + raises the team's limit), We host ($1/GB/mo, and
 * the only option for >100 MB), or Skip ($0). Hosting is a separate recurring
 * subscription, never part of the one-time migration total.
 */
export function MediaChooser({
  tenantId,
  channels,
  channelIds,
  premiumTier,
}: {
  tenantId: Id<"tenants">;
  channels: ChannelScan[];
  channelIds: string[];
  premiumTier: number | null;
}) {
  const tier = asBoostTier(premiumTier) ?? 0;
  const opts = mediaOptions(channels, channelIds, tier);
  const startHosting = useAction(api.checkout.createHostingSubscription);
  const [strategy, setStrategy] = useState<Strategy>("skip");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (opts.overLimit.count === 0) {
    const mb = Math.round(opts.currentCapBytes / (1024 * 1024));
    return (
      <p className="upload-ok mono">
        ✓ All selected media fits your server’s {mb} MB upload limit.
      </p>
    );
  }

  const overGB = opts.overLimit.bytes / GB;
  const capMB = Math.round(opts.currentCapBytes / (1024 * 1024));

  async function host() {
    setBusy(true);
    setError(null);
    try {
      const res = await startHosting({ tenantId, gb: overGB });
      window.location.href = res.url;
    } catch {
      setError("Couldn’t start hosting checkout. Please try again.");
      setBusy(false);
    }
  }

  return (
    <div className="media-chooser">
      <div className="upload-note-head">
        <span aria-hidden="true">⚠️</span>
        <b>
          {opts.overLimit.count.toLocaleString("en-US")} file
          {opts.overLimit.count === 1 ? "" : "s"} · {formatBytes(opts.overLimit.bytes)}{" "}
          exceed your Discord upload limit
        </b>
      </div>
      <p className="muted upload-note-body">
        Your server is <b>{tierLabel(premiumTier)}</b>
        {premiumTier === null && " (couldn’t read its level — assuming Level 0)"}.
        Files over {capMB} MB can’t upload natively. Choose what to do — this
        doesn’t change your one-time migration price.
      </p>

      {opts.crossoverGB !== null && (
        <p className="crossover mono">
          At {opts.crossoverGB} GB over the limit, a{" "}
          {formatUsd(opts.boosts[0].monthlyCents)}/mo boost costs less than hosting.
          You have {overGB.toFixed(1)} GB —{" "}
          {opts.boostCheaperThanHosting ? "boosting is the better deal." : "hosting is cheaper for now."}
        </p>
      )}

      <div className="chooser-opts" role="radiogroup" aria-label="Oversized media">
        <Option
          id="skip"
          checked={strategy === "skip"}
          onSelect={() => setStrategy("skip")}
          title="Skip them"
          price="$0"
          body={`These ${opts.overLimit.count.toLocaleString("en-US")} files won’t migrate.`}
        />

        <Option
          id="host"
          checked={strategy === "host"}
          onSelect={() => setStrategy("host")}
          title="We host + link"
          price={`${formatUsd(opts.hostAllMonthlyCents)}/mo`}
          body={`We host all ${formatBytes(opts.overLimit.bytes)} and link them in your channels (recurring). The only option for files over 100 MB${
            opts.mustHost.bytes > 0 ? ` — you have ${formatBytes(opts.mustHost.bytes)} of those` : ""
          }.`}
        >
          {strategy === "host" && (
            <>
              {error && <p className="form-error">{error}</p>}
              <button className="btn btn-primary btn-sm" onClick={host} disabled={busy}>
                {busy ? "Starting…" : `Set up hosting · ${formatUsd(opts.hostAllMonthlyCents)}/mo`}
              </button>
            </>
          )}
        </Option>

        {opts.boosts.length > 0 && (
          <Option
            id="boost"
            checked={strategy === "boost"}
            onSelect={() => setStrategy("boost")}
            title="Boost your Discord server"
            price={`~${formatUsd(opts.boosts[0].monthlyCents)}/mo`}
            body="Plays files inline and raises the upload limit for your whole team going forward. You boost in Discord, then rescan here."
          >
            {strategy === "boost" && (
              <ul className="boost-list">
                {opts.boosts.map((b) => (
                  <li key={b.toTier}>
                    <b>Level {b.toTier}</b> ({Math.round(b.capBytes / (1024 * 1024))} MB) ·{" "}
                    ~{formatUsd(b.monthlyCents)}/mo — includes{" "}
                    {b.rescuedCount.toLocaleString("en-US")} more file
                    {b.rescuedCount === 1 ? "" : "s"} ({formatBytes(b.rescuedBytes)})
                  </li>
                ))}
                {opts.mustHost.bytes > 0 && (
                  <li className="muted">
                    Files over 100 MB ({formatBytes(opts.mustHost.bytes)}) still need
                    hosting or skipping — no boost lifts that cap.
                  </li>
                )}
              </ul>
            )}
          </Option>
        )}
      </div>

      <p className="upload-note-body upload-note-soon">
        <span className="soon-tag">Coming soon</span> Or offload oversized files to
        your Google Drive and link them in Discord — no boost, no size limit.
      </p>
    </div>
  );
}

function Option({
  id,
  checked,
  onSelect,
  title,
  price,
  body,
  children,
}: {
  id: string;
  checked: boolean;
  onSelect: () => void;
  title: string;
  price: string;
  body: string;
  children?: ReactNode;
}) {
  return (
    <label className="chooser-opt" data-checked={checked}>
      <input
        type="radio"
        name="media-strategy"
        value={id}
        checked={checked}
        onChange={onSelect}
      />
      <span className="chooser-opt-main">
        <span className="chooser-opt-head">
          <b>{title}</b>
          <span className="chooser-opt-price mono">{price}</span>
        </span>
        <span className="chooser-opt-body muted">{body}</span>
        {children}
      </span>
    </label>
  );
}
